import type { BoxProps } from '@mui/material/Box';
import Box from '@mui/material/Box';
import type { SliderProps } from '@mui/material/Slider';
import Slider, { sliderClasses } from '@mui/material/Slider';

import { setFont } from 'minimal-shared/utils';

import type { SettingsState } from '../types';
import { OptionButton } from './styles';

export type FontFamilyOptionsProps = BoxProps & {
    options: string[];
    icon: React.ReactNode;
    value: SettingsState['fontFamily'];
    onChangeOption: (newOption: string) => void;
};

/**
 * Component for selecting font family options.
 *
 * @param props - Component props
 * @param props.sx - Custom styles to apply to the container box
 * @param props.icon - Icon element to display with each option
 * @param props.value - Currently selected font family
 * @param props.options - Array of available font family options
 * @param props.onChangeOption - Callback function when a font family is selected
 * @param props.other - Additional props to pass to the container element
 *
 * @returns A grid of font family options that can be selected
 */
export function FontFamilyOptions({
    sx,
    icon,
    value,
    options,
    onChangeOption,
    ...other
}: FontFamilyOptionsProps) {
    return (
        <Box
            sx={[
                {
                    gap: 1.5,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...other}
        >
            {options.map((option) => {
                const selected = value === option;

                return (
                    <OptionButton
                        key={option}
                        selected={selected}
                        onClick={() => onChangeOption(option)}
                        sx={(theme) => ({
                            py: 2,
                            gap: 0.75,
                            flexDirection: 'column',
                            fontFamily: setFont(option),
                            fontSize: theme.typography.pxToRem(12),
                        })}
                    >
                        {icon}
                        {option.endsWith('Variable') ? option.replace(' Variable', '') : option}
                    </OptionButton>
                );
            })}
        </Box>
    );
}

export type FontSizeOptionsProps = SliderProps & {
    options: [number, number];
    value: SettingsState['fontSize'];
    onChangeOption: (newOption: number) => void;
};

/**
 * A component that renders a slider for adjusting font size.
 *
 * @param {object} props - Component props
 * @param {SxProps<Theme>} [props.sx] - The system prop that allows defining system overrides as well as additional CSS styles
 * @param {number} props.value - The current font size value
 * @param {number[]} props.options - Array containing min and max font size values [min, max]
 * @param {(newOption: number) => void} props.onChangeOption - Callback fired when the font size value changes
 * @returns {React.ReactElement} A slider component for font size selection
 */
export function FontSizeOptions({
    sx,
    value,
    options,
    onChangeOption,
    ...other
}: FontSizeOptionsProps) {
    return (
        <Slider
            marks
            step={1}
            size="small"
            valueLabelDisplay="on"
            aria-label="Change font size"
            valueLabelFormat={(val) => `${val}px`}
            value={value}
            min={options[0]}
            max={options[1]}
            onChange={(_event: Event, newOption: number | number[]) =>
                onChangeOption(newOption as number)
            }
            sx={[
                (theme) => ({
                    [`& .${sliderClasses.rail}`]: {
                        height: 12,
                    },
                    [`& .${sliderClasses.track}`]: {
                        height: 12,
                        background: `linear-gradient(135deg, ${theme.vars.palette.primary.light}, ${theme.vars.palette.primary.dark})`,
                    },
                }),
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...other}
        />
    );
}
